import Dexie, { Table } from 'dexie'

// === New entity types (graph model) ===
export type { Route } from '../schemas/route.js'
export type { Plan } from '../schemas/plan.js'
export type { Activity } from '../schemas/activity.js'
export type { Feature, FeatureStyle, FeatureLabel } from '../schemas/feature.js'
export type { Edge } from '../schemas/edge.js'

// === Legacy types (still used by existing components — removed in later phases) ===
export type { Trip, TripMember } from '../schemas/trip.js'
export type { Track } from '../schemas/track.js'
export type { Waypoint } from '../schemas/waypoint.js'
export type { TripFeature } from '../schemas/trip-feature.js'

// === Shared types ===
export type { LayerGroup, LayerType } from '../schemas/layer-group.js'
export type { User, UserSettings } from '../schemas/user.js'
export type { PackList, PackItem } from '../schemas/pack.js'
export type { Team, TeamMember } from '../schemas/team.js'
export type { OfflineMap } from '../schemas/offline-map.js'
export type { SyncEvent } from '../schemas/sync-event.js'
export type { WeatherCacheEntry } from '../services/weather/weather-types.js'
export type { AvalancheCacheEntry } from '../services/avalanche/avalanche-types.js'
export type { CachedTile } from '../services/tile-cache/tile-cache-types.js'
export type { EmergencyCacheEntry } from '../services/emergency/emergency-types.js'
export type { WildlifeCacheEntry } from '../services/wildlife/wildlife-types.js'
export type { OpenBetaCacheEntry } from '../services/openbeta/openbeta-types.js'
export type { FieldLogEntry } from '../schemas/field-log-entry.js'

// Re-import for Dexie table declarations
import type { User } from '../schemas/user.js'
import type { Route } from '../schemas/route.js'
import type { Plan } from '../schemas/plan.js'
import type { Activity } from '../schemas/activity.js'
import type { Feature } from '../schemas/feature.js'
import type { LayerGroup } from '../schemas/layer-group.js'
import type { Edge } from '../schemas/edge.js'
import type { Trip } from '../schemas/trip.js'
import type { TripFeature } from '../schemas/trip-feature.js'
import type { TripMember } from '../schemas/trip.js'
import type { Track } from '../schemas/track.js'
import type { Waypoint } from '../schemas/waypoint.js'
import type { Team, TeamMember } from '../schemas/team.js'
import type { PackList, PackItem } from '../schemas/pack.js'
import type { OfflineMap } from '../schemas/offline-map.js'
import type { SyncEvent } from '../schemas/sync-event.js'
import type { WeatherCacheEntry } from '../services/weather/weather-types.js'
import type { AvalancheCacheEntry } from '../services/avalanche/avalanche-types.js'
import type { CachedTile } from '../services/tile-cache/tile-cache-types.js'
import type { EmergencyCacheEntry } from '../services/emergency/emergency-types.js'
import type { WildlifeCacheEntry } from '../services/wildlife/wildlife-types.js'
import type { OpenBetaCacheEntry } from '../services/openbeta/openbeta-types.js'
import type { FieldLogEntry } from '../schemas/field-log-entry.js'

// Database class
export class CairnDatabase extends Dexie {
  // User tables
  users!: Table<User>

  // New entity tables (graph model)
  routes!: Table<Route>
  plans!: Table<Plan>
  activities!: Table<Activity>
  features!: Table<Feature>
  edges!: Table<Edge>

  // Legacy tables (still used by existing components — removed in later phases)
  trips!: Table<Trip>
  tripMembers!: Table<TripMember>
  tripFeatures!: Table<TripFeature>
  tracks!: Table<Track>
  waypoints!: Table<Waypoint>

  // Auth token storage (local-only, never synced)
  authTokens!: Table<{ id: string; value: string }>

  // Field log entries (synced as plan children)
  fieldLogEntries!: Table<FieldLogEntry>

  // Local-only tables (never synced)
  weatherCache!: Table<WeatherCacheEntry>
  avalancheCache!: Table<AvalancheCacheEntry>
  tileCache!: Table<CachedTile>
  emergencyCache!: Table<EmergencyCacheEntry>
  wildlifeCache!: Table<WildlifeCacheEntry>
  climbingCache!: Table<OpenBetaCacheEntry>

  // Shared tables
  layerGroups!: Table<LayerGroup>
  teams!: Table<Team>
  teamMembers!: Table<TeamMember>
  packLists!: Table<PackList>
  packItems!: Table<PackItem>
  offlineMaps!: Table<OfflineMap>
  syncEvents!: Table<SyncEvent>
  syncBaseSnapshots!: Table<{
    id: string
    entityType: string
    entityId: string
    data: Record<string, unknown>
    createdAt: string
  }>

  constructor() {
    super('CairnDatabase')

    // Version 171: Add fieldLogEntries table (synced as plan children).
    this.version(171).stores({
      fieldLogEntries: 'id, planId, type, createdBy, createdAt',
    })

    // Version 170: Recover from dev build version drift — no schema changes.
    this.version(170).stores({})

    // Version 15: Add climbingCache table (local-only, never synced).
    this.version(15).stores({
      climbingCache: 'key, fetchedAt',
    })

    // Version 14: Add wildlifeCache table (local-only, never synced).
    this.version(14).stores({
      wildlifeCache: 'key, dataType, fetchedAt',
    })

    // Version 13: Add emergencyCache table (local-only, never synced).
    this.version(13).stores({
      emergencyCache: 'key, dataType, geohash, fetchedAt',
    })

    // Version 12: Remove isPublic from teams (teams are always private).
    this.version(12).stores({
      teams: 'id, name, createdBy, createdAt, updatedAt',
    })

    // Version 11: Replace syncCursors with syncBaseSnapshots for three-way merge.
    this.version(11).stores({
      syncCursors: null,
      syncBaseSnapshots: 'id, [entityType+entityId]',
    })

    // Version 10: Add syncCursors table and version field to entities/edges.
    this.version(10)
      .stores({
        syncCursors: 'id',
      })
      .upgrade(async tx => {
        const tables = ['routes', 'plans', 'activities', 'features', 'edges', 'layerGroups']
        for (const name of tables) {
          await tx
            .table(name)
            .toCollection()
            .modify(item => {
              if (item.version === undefined) {
                item.version = 0
              }
            })
        }
      })

    // Version 9: Add authTokens table for refresh token persistence.
    this.version(9).stores({
      authTokens: 'id',
    })

    // Version 8: Add tileCache table (local-only, never synced).
    // Stores raster/vector tile blobs with SWR semantics and LRU eviction.
    // pinned is 0|1 because IndexedDB cannot index boolean keys.
    this.version(8).stores({
      tileCache: 'key, sourceId, [sourceId+z], pinned, lastAccessedAt',
    })

    // Version 7: Add avalancheCache table (local-only, never synced).
    this.version(7).stores({
      avalancheCache: 'id',
    })

    // Version 5: Add tags multi-entry index on features. Backfill Route-per-Plan.
    this.version(5)
      .stores({
        users: 'id, email, displayName, createdAt',
        routes: 'id, name, createdBy, createdAt, updatedAt',
        plans: 'id, name, createdBy, startDate, endDate, createdAt, updatedAt',
        activities: 'id, name, createdBy, activityType, startTime, endTime, createdAt, updatedAt',
        features: 'id, name, type, *tags, isVisible, waypointType, createdBy, createdAt, updatedAt',
        edges:
          'id, [subject+predicate], [object+predicate], [predicate+subject], [predicate+object], subject, object, predicate, createdAt',
        trips:
          'id, name, createdBy, status, dateRange.startDate, dateRange.endDate, createdAt, updatedAt',
        tripMembers: '[tripId+userId], tripId, userId, role, status, joinedAt',
        tripFeatures: 'id, name, type, layerId, isVisible, createdAt, updatedAt, createdBy',
        tracks:
          'id, tripId, name, type, pointCount, distance, startTime, endTime, duration, createdAt, updatedAt',
        waypoints: 'id, tripId, name, type, createdAt, updatedAt',
        layerGroups:
          'id, name, color, layerType, isSystemGroup, parentLayerId, tripId, displayOrder, isVisible, isExpanded, createdAt, updatedAt, createdBy',
        teams: 'id, name, createdBy, isPublic, createdAt, updatedAt',
        teamMembers: '[teamId+userId], teamId, userId, role, status, joinedAt',
        packLists: 'id, name, createdBy, isTemplate, isPublic, category, createdAt, updatedAt',
        packItems: 'id, packListId, name, category, isEssential, assignedTo, packed',
        offlineMaps: 'id, name, downloadedAt, expiresAt, size',
        syncEvents: 'id, entityType, entityId, operation, timestamp, syncedAt, conflictResolved',
        weatherCache: 'id',
      })
      .upgrade(async tx => {
        // Backfill tags on existing features
        await tx
          .table('features')
          .toCollection()
          .modify(feature => {
            feature.tags ??= []
          })

        // Backfill Route-per-Plan: create a Route for each plan that doesn't have one
        const plans = await tx.table('plans').toArray()
        const edges = tx.table('edges')
        const routes = tx.table('routes')
        const layerGroups = tx.table('layerGroups')

        for (const plan of plans) {
          // Check if plan already has a uses-route edge
          const existing = await edges
            .where('[subject+predicate]')
            .equals([`plan:${plan.id}`, 'uses-route'])
            .first()
          if (existing) continue

          const now = new Date().toISOString()
          const routeId = crypto.randomUUID()
          const groupId = crypto.randomUUID()

          // Create Route entity
          await routes.add({
            id: routeId,
            name: `${plan.name} Route`,
            origin: { type: 'user-created' },
            tags: [],
            isPublic: false,
            createdBy: plan.createdBy,
            createdAt: now,
            updatedAt: now,
          })

          // Create root layer group for the route
          await layerGroups.add({
            id: groupId,
            name: `${plan.name} Route`,
            color: '#3b82f6',
            layerType: 'features',
            isSystemGroup: true,
            displayOrder: 0,
            isVisible: true,
            isExpanded: true,
            createdBy: plan.createdBy,
            createdAt: now,
            updatedAt: now,
          })

          // Wire edges: route has-layer-group, plan uses-route
          await edges.bulkAdd([
            {
              id: crypto.randomUUID(),
              subject: `route:${routeId}`,
              predicate: 'has-layer-group',
              object: `group:${groupId}`,
              createdAt: now,
            },
            {
              id: crypto.randomUUID(),
              subject: `plan:${plan.id}`,
              predicate: 'uses-route',
              object: `route:${routeId}`,
              createdAt: now,
            },
          ])
        }
      })

    // Version 6: Add layerId index to features table for layer-group-manager migration.
    // Copies tripFeatures into features (layerId, tripIds) so layer tree works from features table.
    this.version(6)
      .stores({
        features:
          'id, name, type, *tags, layerId, isVisible, waypointType, createdBy, createdAt, updatedAt',
      })
      .upgrade(async tx => {
        const tripFeatures = await tx.table('tripFeatures').toArray()
        const features = tx.table('features')

        for (const tf of tripFeatures) {
          const existing = await features.get(tf.id)
          if (existing) {
            // Feature already exists in new table — merge layerId/tripIds
            await features.update(tf.id, {
              layerId: tf.layerId,
              tripIds: tf.tripIds,
            })
          } else {
            // Copy TripFeature into features table with all fields
            await features.add({
              id: tf.id,
              name: tf.name,
              description: tf.description,
              type: tf.type,
              geometry: tf.geometry,
              properties: tf.properties,
              style: tf.style,
              label: tf.label,
              tags: tf.tags ?? [],
              isVisible: tf.isVisible,
              bounds: tf.bounds,
              layerId: tf.layerId,
              tripIds: tf.tripIds,
              createdBy: tf.createdBy,
              createdAt: tf.createdAt,
              updatedAt: tf.updatedAt,
            })
          }
        }
      })

    // Version 4: Add weatherCache table (local-only, never synced).
    // No timestamp hooks — manages its own fetchedAt/refreshAfter.
    this.version(4).stores({
      // All existing tables carry forward unchanged
      users: 'id, email, displayName, createdAt',
      routes: 'id, name, createdBy, createdAt, updatedAt',
      plans: 'id, name, createdBy, startDate, endDate, createdAt, updatedAt',
      activities: 'id, name, createdBy, activityType, startTime, endTime, createdAt, updatedAt',
      features: 'id, name, type, isVisible, waypointType, createdBy, createdAt, updatedAt',
      edges:
        'id, [subject+predicate], [object+predicate], [predicate+subject], [predicate+object], subject, object, predicate, createdAt',
      trips:
        'id, name, createdBy, status, dateRange.startDate, dateRange.endDate, createdAt, updatedAt',
      tripMembers: '[tripId+userId], tripId, userId, role, status, joinedAt',
      tripFeatures: 'id, name, type, layerId, isVisible, createdAt, updatedAt, createdBy',
      tracks:
        'id, tripId, name, type, pointCount, distance, startTime, endTime, duration, createdAt, updatedAt',
      waypoints: 'id, tripId, name, type, createdAt, updatedAt',
      layerGroups:
        'id, name, color, layerType, isSystemGroup, parentLayerId, tripId, displayOrder, isVisible, isExpanded, createdAt, updatedAt, createdBy',
      teams: 'id, name, createdBy, isPublic, createdAt, updatedAt',
      teamMembers: '[teamId+userId], teamId, userId, role, status, joinedAt',
      packLists: 'id, name, createdBy, isTemplate, isPublic, category, createdAt, updatedAt',
      packItems: 'id, packListId, name, category, isEssential, assignedTo, packed',
      offlineMaps: 'id, name, downloadedAt, expiresAt, size',
      syncEvents: 'id, entityType, entityId, operation, timestamp, syncedAt, conflictResolved',
      // New: local-only weather cache
      weatherCache: 'id',
    })

    // Version 3: Add graph-based entity model tables alongside legacy tables.
    // Legacy tables will be removed in a later phase once all consumers are migrated.
    this.version(3).stores({
      // User tables
      users: 'id, email, displayName, createdAt',

      // New entity tables (graph model)
      routes: 'id, name, createdBy, createdAt, updatedAt',
      plans: 'id, name, createdBy, startDate, endDate, createdAt, updatedAt',
      activities: 'id, name, createdBy, activityType, startTime, endTime, createdAt, updatedAt',
      features: 'id, name, type, isVisible, waypointType, createdBy, createdAt, updatedAt',
      edges:
        'id, [subject+predicate], [object+predicate], [predicate+subject], [predicate+object], subject, object, predicate, createdAt',

      // Legacy tables (unchanged — removed in later phase)
      trips:
        'id, name, createdBy, status, dateRange.startDate, dateRange.endDate, createdAt, updatedAt',
      tripMembers: '[tripId+userId], tripId, userId, role, status, joinedAt',
      tripFeatures: 'id, name, type, layerId, isVisible, createdAt, updatedAt, createdBy',
      tracks:
        'id, tripId, name, type, pointCount, distance, startTime, endTime, duration, createdAt, updatedAt',
      waypoints: 'id, tripId, name, type, createdAt, updatedAt',

      // Shared tables (layerGroups keeps old indices until Phase 3 migration)
      layerGroups:
        'id, name, color, layerType, isSystemGroup, parentLayerId, tripId, displayOrder, isVisible, isExpanded, createdAt, updatedAt, createdBy',
      teams: 'id, name, createdBy, isPublic, createdAt, updatedAt',
      teamMembers: '[teamId+userId], teamId, userId, role, status, joinedAt',
      packLists: 'id, name, createdBy, isTemplate, isPublic, category, createdAt, updatedAt',
      packItems: 'id, packListId, name, category, isEssential, assignedTo, packed',
      offlineMaps: 'id, name, downloadedAt, expiresAt, size',
      syncEvents: 'id, entityType, entityId, operation, timestamp, syncedAt, conflictResolved',
    })

    this.version(2).stores({
      users: 'id, email, displayName, createdAt',
      trips:
        'id, name, createdBy, status, dateRange.startDate, dateRange.endDate, createdAt, updatedAt',
      tripMembers: '[tripId+userId], tripId, userId, role, status, joinedAt',
      tripFeatures: 'id, name, type, layerId, isVisible, createdAt, updatedAt, createdBy',
      layerGroups:
        'id, name, color, layerType, isSystemGroup, parentLayerId, tripId, displayOrder, isVisible, isExpanded, createdAt, updatedAt, createdBy',
      teams: 'id, name, createdBy, isPublic, createdAt, updatedAt',
      teamMembers: '[teamId+userId], teamId, userId, role, status, joinedAt',
      packLists: 'id, name, createdBy, isTemplate, isPublic, category, createdAt, updatedAt',
      packItems: 'id, packListId, name, category, isEssential, assignedTo, packed',
      tracks:
        'id, tripId, name, type, pointCount, distance, startTime, endTime, duration, createdAt, updatedAt',
      waypoints: 'id, tripId, name, type, createdAt, updatedAt',
      offlineMaps: 'id, name, downloadedAt, expiresAt, size',
      syncEvents: 'id, entityType, entityId, operation, timestamp, syncedAt, conflictResolved',
    })

    this.version(1).stores({
      users: 'id, email, displayName, createdAt',
      trips:
        'id, name, createdBy, status, dateRange.startDate, dateRange.endDate, createdAt, updatedAt',
      tripMembers: '[tripId+userId], tripId, userId, role, status, joinedAt',
      teams: 'id, name, createdBy, isPublic, createdAt, updatedAt',
      teamMembers: '[teamId+userId], teamId, userId, role, status, joinedAt',
      packLists: 'id, name, createdBy, isTemplate, isPublic, category, createdAt, updatedAt',
      packItems: 'id, packListId, name, category, isEssential, assignedTo, packed',
      tracks:
        'id, tripId, name, type, pointCount, distance, startTime, endTime, duration, createdAt, updatedAt',
      waypoints: 'id, tripId, name, type, createdAt, updatedAt',
      offlineMaps: 'id, name, downloadedAt, expiresAt, size',
      syncEvents: 'id, entityType, entityId, operation, timestamp, syncedAt, conflictResolved',
    })

    // Automatic timestamps for new entity tables
    const addTimestampHooks = (table: Table): void => {
      table.hook('creating', (_primKey, obj) => {
        obj.createdAt = new Date().toISOString()
        obj.updatedAt = obj.createdAt
      })
      table.hook('updating', modifications => {
        ;(modifications as Record<string, unknown>).updatedAt = new Date().toISOString()
      })
    }

    addTimestampHooks(this.fieldLogEntries)
    addTimestampHooks(this.routes)
    addTimestampHooks(this.plans)
    addTimestampHooks(this.activities)
    addTimestampHooks(this.features)
    addTimestampHooks(this.layerGroups)

    // Legacy table hooks (kept until tables are removed)
    addTimestampHooks(this.trips)
    addTimestampHooks(this.tracks)
    addTimestampHooks(this.tripFeatures)
    addTimestampHooks(this.packLists)
  }
}

// Create and export database instance
export const db = new CairnDatabase()
